/**
 * Pure helpers for the PostHog capture pipeline, kept out of `posthog.client.ts`
 * so they can be exercised without booting the SDK.
 */

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const CUID_RE = /\bc[a-z0-9]{24}\b/gi;

/**
 * Collapses dynamic route segments (numeric ids, UUIDs, Prisma cuids) to `:id`
 * so per-tool usage counts don't fragment across thousands of distinct paths
 * (e.g. `/homes/abc123/tasks` and `/homes/def456/tasks` both roll up to
 * `/homes/:id/tasks`).
 */
export function normalizePath(path: string): string {
  return path
    .split('/')
    .map((seg) =>
      /^\d+$/.test(seg) ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
      /^c[a-z0-9]{24}$/i.test(seg)
        ? ':id'
        : seg
    )
    .join('/');
}

/**
 * Exception messages that are pure browser noise and must never reach the
 * stream. `ResizeObserver loop …` is fired by the browser when a resize handler
 * schedules another layout pass; it is not an error, nothing is broken, and it
 * has been documented as non-actionable since HHOS-1879 — but at ~57 events a
 * week on `/messaging` alone it crowds out defects that matter (HHOS-1966).
 */
const IGNORED_EXCEPTION_PATTERNS = [/ResizeObserver loop/i];

export function isIgnoredExceptionMessage(message: string): boolean {
  return IGNORED_EXCEPTION_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Strips high-cardinality ids out of an exception message so every instance of
 * one defect groups into a single row.
 *
 * PostHog buckets exceptions by their message text, so an id in the message
 * shatters one bug across as many rows as it has ids. Ably's
 * `Channel denied access based on given capability; channelId = home:<uuid>`
 * was the worst case: a single realtime failure appeared as fifteen
 * unremarkable rows instead of the largest error source in the app, and went
 * unnoticed for ~800 events (HHOS-1966).
 */
export function normalizeExceptionMessage(message: string): string {
  return message.replace(UUID_RE, '<id>').replace(CUID_RE, '<id>');
}

/**
 * Query parameters that carry customer PII and must never reach the event
 * stream, even though the page they belong to is staff-only.
 *
 * `pinLabel` is the formatted street address of an address searched on the
 * region map. A shared map link puts it in the URL — and therefore in
 * `$current_url` on every autocapture, pageview, web-vital and exception
 * fired afterwards, for the sender AND for everyone who opens the link. The
 * region map's own events reduce a searched address to a coverage verdict and
 * a bucketed distance for exactly this reason; leaving the raw value in the
 * URL would route around that.
 */
// `q` on /tools is a job an operator described in their own words, which sometimes names a member.
const REDACTED_QUERY_PARAMS = ['pinLabel', 'q'];

/** Replaces any PII-bearing query parameter with `<redacted>`, leaving the rest of the URL intact. */
export function redactUrlParams(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    for (const param of REDACTED_QUERY_PARAMS) {
      if (url.searchParams.has(param)) url.searchParams.set(param, '<redacted>');
    }
    return url.toString();
  } catch {
    return rawUrl;
  }
}
